import type { StepperProps } from '@packages/shared';
import { Box, Stepper } from '@packages/shared';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import {
  Cart32,
  CheckOrder32,
  Delivery32,
  Invoice32,
  LoginClosed32,
  LoginOpened32,
} from '@packages/themes/icons';

import { defineMessages, useIntl } from 'react-intl';
import { Page } from '@empiriecom/mybuy-frontend-api/backend-mybuy-checkout/v1/models/Page';
import { useRouter } from 'next/navigation';

const messages = defineMessages({
  basket: {
    id: 'ProgressBar.basket',
    defaultMessage: 'Warenkorb',
  },
  login: {
    id: 'ProgressBar.login',
    defaultMessage: 'Anmeldung',
  },
  delivery: {
    id: 'ProgressBar.delivery',
    defaultMessage: 'Lieferung',
  },
  payment: {
    id: 'ProgressBar.payment',
    defaultMessage: 'Zahlung',
  },
  checkorder: {
    id: 'ProgressBar.checkorder',
    defaultMessage: 'Prüfen & Bestellen',
  },
});

export const ProgressBar = ({
  activeStep,
  saveAndNavigate,
}: {
  activeStep: number;
  saveAndNavigate?: (targetPage: Page) => Promise<any>;
}) => {
  const { formatMessage, locale } = useIntl();
  const router = useRouter();
  const {
    order: { paths },
  } = useConfig();
  const { loggedIn } = useLoginState();

  const checkoutSteps: Array<{
    name: string;
    icon: React.ReactElement<any>;
    localizedPath: string;
  }> = [
    {
      name: formatMessage(messages.basket),
      icon: <Cart32 />,
      localizedPath: getLocalizedPath(paths.basket, locale),
    },
    {
      name: formatMessage(messages.login),
      icon: loggedIn ? <LoginOpened32 /> : <LoginClosed32 />,
      localizedPath: getLocalizedPath(paths.login, locale),
    },
    {
      name: formatMessage(messages.delivery),
      icon: <Delivery32 />,
      localizedPath: getLocalizedPath(paths.delivery, locale),
    },
    {
      name: formatMessage(messages.payment),
      icon: <Invoice32 />,
      localizedPath: getLocalizedPath(paths.payment, locale),
    },
    {
      name: formatMessage(messages.checkorder),
      icon: <CheckOrder32 />,
      localizedPath: getLocalizedPath(paths.checkandorder, locale),
    },
  ];

  const steps = checkoutSteps.map((value) => value.name);

  /**
   * results in:
   * {
   *   1: <Cart32 />
   *   2: <LoginOpened32 />
   *   3: ...
   * }
   */
  const icons = checkoutSteps
    .map((value, index) => ({ [index + 1]: value.icon }))
    .reduce((prev, next) => ({ ...prev, ...next }), {});

  const onClickHandler: StepperProps['handleOnClick'] = (index: number) => {
    // todo implement saving data when we need it for checkout steps
    saveAndNavigate?.(Page.Basket);
    router.push(checkoutSteps[index].localizedPath);
    return undefined;
  };

  return (
    <Box
      sx={{
        width: '100%',
        pt: 2,
        pb: { xs: '0.75rem', md: '1.805rem' },
        px: { xs: '1rem', md: '0' },
      }}
    >
      <Stepper activeStep={activeStep} steps={steps} icons={icons} handleOnClick={onClickHandler} />
    </Box>
  );
};
